/**
 * Pure helpers for the sidebar board tree. No store or Vue dependencies.
 *
 * - compareRank: order two ranked rows by (rank, id).
 * - upsertRankedById: replace-or-append a ranked row, keeping (rank, id) order.
 * - buildTreeRows: flatten folders and boards into renderable, indented rows.
 * - resolveTreeDrop: turn a drop position in the flat rows into a parent and placement.
 * - currentTreePlacement: a row's present parent and same-kind neighbors.
 */
import type {IBoard} from '@models/board';
import type {IBoardFolder} from '@models/board_folder';
import {DEFAULT_BOARD_COLOR} from '@shared/board_types';
import type {IRankPlacement} from '@shared/board_types';

export enum TreeRowKind {
   FOLDER = 'folder',
   BOARD = 'board',
}

/** One rendered row of the sidebar tree, folder or board. */
export interface ITreeRow {
   key: string;
   kind: TreeRowKind;
   id: string;
   /** Folder the row sits in; null at the organization's top level. */
   parentFolderId: string | null;
   name: string;
   color: string;
   depth: number;
   /** Boards inside the folder at any depth; empty for boards and empty folders. */
   countLabel: string;
   isActive: boolean;
   isOpen: boolean;
}

/** Where a tree row sits or lands: its parent and the same-kind siblings around it. */
export interface ITreeDrop {
   parentFolderId: string | null;
   /** Null appends after the parent's last row of the same kind. */
   placement: IRankPlacement | null;
}

interface IRanked {
   id: string;
   rank: string;
}

/**
 * Order two ranked rows: by rank, then by id as the tie-break for equal ranks. Ranks are
 * compared as byte strings, matching the COLLATE "C" column.
 */
export function compareRank(a: IRanked, b: IRanked): number {
   if (a.rank !== b.rank) {
      return a.rank < b.rank ? -1 : 1;
   }
   if (a.id === b.id) {
      return 0;
   }
   return a.id < b.id ? -1 : 1;
}

/** Return rows with the row replaced by id, or added when absent, in (rank, id) order. */
export function upsertRankedById<T extends IRanked>(rows: readonly T[], row: T): T[] {
   const updated = rows.filter((existing) => existing.id !== row.id);
   updated.push(row);
   updated.sort(compareRank);
   return updated;
}

/**
 * Flatten the organization's folders and boards into tree rows in display order:
 * - at each level folders first, then boards, both by (rank, id).
 * - Closed folders hide their contents.
 * - A non-empty filter keeps only boards whose name contains it and forces ancestor folders open
 */
export function buildTreeRows(opts: {
   folders: readonly IBoardFolder[];
   boards: readonly IBoard[];
   openFolderIds: readonly string[];
   activeBoardId: string | null;
   filterText: string;
}): ITreeRow[] {
   const query = opts.filterText.trim().toLowerCase();
   const is_filtering = query.length > 0;
   const open_ids = new Set(opts.openFolderIds);

   const folders_by_parent = groupByParent(opts.folders, (folder) => folder.parentFolderId ?? null);
   const boards_by_folder = groupByParent(opts.boards, (board) => board.folderId ?? null);

   const board_matches = (board: IBoard): boolean => {
      return !is_filtering || board.name.toLowerCase().includes(query);
   };

   const count_matching_boards = (folder_id: string): number => {
      const own = (boards_by_folder.get(folder_id) ?? []).filter(board_matches).length;
      const nested = (folders_by_parent.get(folder_id) ?? []).reduce(
         (sum, child) => sum + count_matching_boards(child.id),
         0,
      );
      return own + nested;
   };

   const rows: ITreeRow[] = [];

   const walk = (parent_id: string | null, depth: number): void => {
      for (const folder of folders_by_parent.get(parent_id) ?? []) {
         const board_count = count_matching_boards(folder.id);
         if (is_filtering && board_count === 0) {
            continue;
         }
         const is_open = is_filtering || open_ids.has(folder.id);
         rows.push({
            key: `f-${folder.id}`,
            kind: TreeRowKind.FOLDER,
            id: folder.id,
            parentFolderId: parent_id,
            name: folder.name,
            color: folder.color ?? DEFAULT_BOARD_COLOR,
            depth,
            countLabel: board_count > 0 ? String(board_count) : '',
            isActive: false,
            isOpen: is_open,
         });
         if (is_open) {
            walk(folder.id, depth + 1);
         }
      }

      for (const board of boards_by_folder.get(parent_id) ?? []) {
         if (!board_matches(board)) {
            continue;
         }
         rows.push({
            key: `b-${board.id}`,
            kind: TreeRowKind.BOARD,
            id: board.id,
            parentFolderId: parent_id,
            name: board.name,
            color: board.color ?? DEFAULT_BOARD_COLOR,
            depth,
            countLabel: '',
            isActive: board.id === opts.activeBoardId,
            isOpen: false,
         });
      }
   };

   walk(null, 0);
   return rows;
}

/**
 * Resolve where the row at newIndex landed after a drag.
 * - `rows` are the flat rows as the drag left them (the moved row at newIndex);
 * - `fullRows` are the tree's current rows, which may show folders opened during the drag.
 *
 * The row above decides the parent: an open folder (open in the rows or in openFolderIds)
 * makes the moved row its first child,
 * any other row makes it a sibling under that row's parent, nothing above means the top
 * level. Neighbors are the nearest rows of the same kind under that parent.
 *
 * Returns null when a folder would land inside itself or one of its descendants.
 */
export function resolveTreeDrop(opts: {
   rows: readonly ITreeRow[];
   newIndex: number;
   openFolderIds: readonly string[];
   fullRows: readonly ITreeRow[];
}): ITreeDrop | null {
   const {rows, newIndex, fullRows} = opts;
   const moved = rows[newIndex];
   if (moved == null) {
      return null;
   }

   const open_ids = new Set(opts.openFolderIds);
   const above = newIndex > 0 ? rows[newIndex - 1] : null;
   const lands_as_first_child =
      above != null &&
      above.kind === TreeRowKind.FOLDER &&
      (above.isOpen || open_ids.has(above.id));
   const parent_folder_id = lands_as_first_child ? above.id : (above?.parentFolderId ?? null);

   if (
      moved.kind === TreeRowKind.FOLDER &&
      isFolderOrDescendant([...rows, ...fullRows], parent_folder_id, moved.id)
   ) {
      return null;
   }

   const is_sibling = (row: ITreeRow): boolean =>
      row.kind === moved.kind && row.parentFolderId === parent_folder_id && row.id !== moved.id;

   if (lands_as_first_child) {
      // The folder's children may not be in `rows` yet; the tree's rows have them.
      const next = fullRows.find(is_sibling) ?? null;
      return {
         parentFolderId: parent_folder_id,
         placement: next == null ? null : {prevId: null, nextId: next.id},
      };
   }

   let prev_id: string | null = null;
   for (let i = newIndex - 1; i >= 0; i--) {
      if (is_sibling(rows[i]!)) {
         prev_id = rows[i]!.id;
         break;
      }
   }

   // A parent's subtree is contiguous in depth-first order, so the first matching row
   // forward is the next sibling.
   let next_id: string | null = null;
   for (let i = newIndex + 1; i < rows.length; i++) {
      if (is_sibling(rows[i]!)) {
         next_id = rows[i]!.id;
         break;
      }
   }

   const placement = prev_id == null && next_id == null ? null : {prevId: prev_id, nextId: next_id};
   return {parentFolderId: parent_folder_id, placement};
}

/**
 * Get a row's present parent and same-kind neighbors in the given rows; null when the row
 * is not in them. Compare against a resolved drop to skip moves that change nothing.
 */
export function currentTreePlacement(rows: readonly ITreeRow[], rowId: string): ITreeDrop | null {
   const index = rows.findIndex((row) => row.id === rowId);
   if (index === -1) {
      return null;
   }
   const row = rows[index]!;
   const is_sibling = (other: ITreeRow): boolean =>
      other.kind === row.kind && other.parentFolderId === row.parentFolderId && other.id !== row.id;

   let prev_id: string | null = null;
   for (let i = index - 1; i >= 0; i--) {
      if (is_sibling(rows[i]!)) {
         prev_id = rows[i]!.id;
         break;
      }
   }
   let next_id: string | null = null;
   for (let i = index + 1; i < rows.length; i++) {
      if (is_sibling(rows[i]!)) {
         next_id = rows[i]!.id;
         break;
      }
   }

   const placement = prev_id == null && next_id == null ? null : {prevId: prev_id, nextId: next_id};
   return {parentFolderId: row.parentFolderId, placement};
}

/** True when two drops name the same parent and the same neighbors. */
export function isSameTreePlacement(a: ITreeDrop, b: ITreeDrop): boolean {
   return (
      a.parentFolderId === b.parentFolderId &&
      (a.placement?.prevId ?? null) === (b.placement?.prevId ?? null) &&
      (a.placement?.nextId ?? null) === (b.placement?.nextId ?? null)
   );
}

// ---- helpers ---- //

function groupByParent<T extends IRanked>(
   rows: readonly T[],
   parentOf: (row: T) => string | null,
): Map<string | null, T[]> {
   const groups = new Map<string | null, T[]>();
   for (const row of rows) {
      const parent = parentOf(row);
      const group = groups.get(parent);
      if (group == null) {
         groups.set(parent, [row]);
      } else {
         group.push(row);
      }
   }
   for (const group of groups.values()) {
      group.sort(compareRank);
   }
   return groups;
}

/** True when folderId is targetId or sits anywhere under it, walking the rows' parent pointers. */
function isFolderOrDescendant(
   rows: readonly ITreeRow[],
   folderId: string | null,
   targetId: string,
): boolean {
   const parent_of = new Map<string, string | null>();
   for (const row of rows) {
      if (row.kind === TreeRowKind.FOLDER && !parent_of.has(row.id)) {
         parent_of.set(row.id, row.parentFolderId);
      }
   }

   const seen = new Set<string>();
   let current = folderId;
   while (current != null && !seen.has(current)) {
      if (current === targetId) {
         return true;
      }
      seen.add(current);
      current = parent_of.get(current) ?? null;
   }
   return false;
}
